
import { useEffect, useState } from 'react';
import { defaultPaymentSettings, savePaymentSettings } from '../../../../models/paymentSettings';
import Checkbox from '../../../../components/Checkbox';
import BackWithSidebar from '../../../../components/BackWithSidebar';
import { useToast } from '../../../../components/ToastContext';

export default function Formas_pagamento() {
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [data, setData] = useState(() => JSON.parse(JSON.stringify(defaultPaymentSettings)));
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');
	const toast = useToast();

	useEffect(() => {
		// no API loading here yet, so just simulate loading
		// try to load any local draft first
		try {
			const draft = window.localStorage.getItem('paymentSettingsDraft');
			if (draft) {
				setData(JSON.parse(draft));
				toast.showInfo && toast.showInfo('Rascunho de formas de pagamento carregado localmente.');
			} else {
				const saved = window.localStorage.getItem('paymentSettings');
				if (saved) setData(JSON.parse(saved));
			}
		} catch (e) {
			// ignore parse errors
		}
		setLoading(false);
	}, [toast]);

	const togglePresencial = (val) => {
		setData((d) => ({
			...d,
			presencial: {
				...d.presencial,
				enabled: val,
				methods: val ? { ...d.presencial.methods, dinheiro: true } : { ...d.presencial.methods, dinheiro: false },
			},
		}));
		setError('');
	};

	const toggleAplicativo = (val) => {
		setData((d) => ({ ...d, aplicativo: { ...d.aplicativo, enabled: val } }));
		setError('');
	};

	const setPresencialMethods = (key, val) => {
		setData((d) => ({ ...d, presencial: { ...d.presencial, methods: { ...d.presencial.methods, [key]: val } } }));
	};

	const setAplicativoMethods = (key, val) => {
		setData((d) => ({ ...d, aplicativo: { ...d.aplicativo, methods: { ...d.aplicativo.methods, [key]: val } } }));
	};

	const valid = () => {
		// if any of presencial/aplicativo enabled must have at least one method enabled
		if (data.presencial.enabled) {
			if (!Object.values(data.presencial.methods).some(Boolean)) return false;
		}
		if (data.aplicativo.enabled) {
			if (!Object.values(data.aplicativo.methods).some(Boolean)) return false;
		}
		// at least one of presencial/aplicativo should be enabled
		if (!data.presencial.enabled && !data.aplicativo.enabled) return false;
		return true;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');
		setSuccess('');
		if (!valid()) {
			setError('É necessário deixar ao menos uma forma de pagamento habilitada.');
			return;
		}

		setSaving(true);
		try {
			const res = await savePaymentSettings(data);
			// res can be { saved: false, draft } or parsed JSON or { saved: true }
			if (res && res.saved === false) {
				toast.showInfo('Rascunho salvo localmente — será reenviado quando possível.');
				setSuccess('Rascunho salvo localmente.');
				// also persist the current settings as the last-known local copy
				try { window.localStorage.setItem('paymentSettings', JSON.stringify(data)); } catch (e) {}
			} else {
				toast.showSuccess('Configurações salvas com sucesso.');
				setSuccess('Configurações salvas.');
				// remove any draft marker and persist canonical saved copy
				try { window.localStorage.removeItem('paymentSettingsDraft'); window.localStorage.setItem('paymentSettings', JSON.stringify(data)); } catch (e) {}
			}
		} catch (err) {
			console.error('savePaymentSettings failed', err);
			toast.showError('Falha ao salvar as configurações.');
			setError('Falha ao salvar as configurações.');
		} finally {
			setSaving(false);
		}
	};

	// Back navigation handled by BackWithSidebar component (see below)

	if (loading) return <div className="p-6">Carregando...</div>;

	return (
		<div className="min-h-screen bg-white flex items-center justify-center p-6">
			<div className="w-full max-w-lg bg-white">
				<div className="flex items-center gap-3 mb-6">
					<BackWithSidebar to="/dashboard" className="p-2 rounded-full hover:bg-gray-100" />
					<h1 className="text-lg font-semibold">Formas de Pagamento</h1>
				</div>

				<form onSubmit={handleSubmit} className="w-full">
					<div className="mb-6">
						<h3 className="font-medium mb-3">Pagamento presencial</h3>
						<div className="mb-3">
							<Checkbox label="Habilitado" checked={data.presencial.enabled} onChange={(e) => togglePresencial(e.target.checked)} />
						</div>

					{data.presencial.enabled && (
						<div className="pl-6">
							<label className="block mb-2 text-sm">Quantidade máxima de parcelas</label>
							<input type="number" value={data.presencial.maxInstallments} min={1} onChange={(e) => setData(d => ({...d, presencial: {...d.presencial, maxInstallments: Number(e.target.value)}}))} className="w-full p-2 border rounded mb-3" />

							<div className="text-sm text-gray-700 mb-2">Métodos de pagamento aceitos</div>
							<div className="space-y-2">
								<Checkbox
									label="Dinheiro"
									checked={true}
									disabled={true}
									checkedClass="peer-checked:bg-purple-400"
								/>
								<Checkbox label="Pix" checked={data.presencial.methods.pix} onChange={(e) => setPresencialMethods('pix', e.target.checked)} />
								<Checkbox label="Cartão de débito" checked={data.presencial.methods.debito} onChange={(e) => setPresencialMethods('debito', e.target.checked)} />
								<Checkbox label="Cartão de crédito" checked={data.presencial.methods.credito} onChange={(e) => setPresencialMethods('credito', e.target.checked)} />
							</div>
						</div>
					)}
				</div>

				<div className="mb-6">
					<h3 className="font-medium mb-3">Pagamento pelo aplicativo</h3>
					<div className="mb-3">
						<Checkbox label="Habilitado" checked={data.aplicativo.enabled} onChange={(e) => toggleAplicativo(e.target.checked)} />
					</div>

					{data.aplicativo.enabled && (
						<div className="pl-6">
							<label className="block mb-2 text-sm">Quantidade máxima de parcelas</label>
							<input type="number" value={data.aplicativo.maxInstallments} min={1} onChange={(e) => setData(d => ({...d, aplicativo: {...d.aplicativo, maxInstallments: Number(e.target.value)}}))} className="w-full p-2 border rounded mb-3" />

							<div className="text-sm text-gray-700 mb-2">Métodos de pagamento aceitos</div>
							<div className="space-y-2">
								<Checkbox label="Pix" checked={data.aplicativo.methods.pix} onChange={(e) => setAplicativoMethods('pix', e.target.checked)} />
								<Checkbox label="Cartão de débito" checked={data.aplicativo.methods.debito} onChange={(e) => setAplicativoMethods('debito', e.target.checked)} />
								<Checkbox label="Cartão de crédito" checked={data.aplicativo.methods.credito} onChange={(e) => setAplicativoMethods('credito', e.target.checked)} />
							</div>
						</div>
					)}
				</div>

				{error && <div className="text-sm text-red-500 mb-4">{error}</div>}
				{success && <div className="text-sm text-green-600 mb-4">{success}</div>}

				<button type="submit" disabled={saving} className="w-full py-2 rounded bg-purple-300 text-white disabled:opacity-50 hover:bg-purple-600">
					{saving ? 'Salvando...' : 'Atualizar'}
				</button>
			</form>
		</div>
	
        </div>)}

